import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { fromNow } from "@/lib/format";
import { Bell, Check, BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { EmptyState } from "@/components/common";
import { toast } from "sonner";
import { enablePush, notificationPermission, pushSupported } from "@/lib/push";

function beep() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = "sine"; o.frequency.value = 880;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.14, ctx.currentTime + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);
    o.start(); o.stop(ctx.currentTime + 0.32);
    o.onended = () => ctx.close();
  } catch { /* ignore */ }
}

export default function NotificationBell() {
  const nav = useNavigate();
  const qc = useQueryClient();
  const seenRef = useRef(null);
  const [perm, setPerm] = useState(notificationPermission());

  const { data = [] } = useQuery({
    queryKey: ["notifications"],
    queryFn: async () => (await api.get("/notifications")).data,
    refetchInterval: 20000,
  });
  const unread = data.filter((n) => !n.read).length;

  useEffect(() => {
    const ids = new Set(data.map((n) => n.id));
    if (seenRef.current === null) { seenRef.current = ids; return; }
    const fresh = data.filter((n) => !seenRef.current.has(n.id) && !n.read);
    if (fresh.length) {
      beep();
      if (navigator.vibrate) { try { navigator.vibrate([80, 40, 80]); } catch { /* ignore */ } }
      fresh.slice(0, 3).forEach((n) =>
        toast(n.title, { description: n.body, action: n.link ? { label: "Aç", onClick: () => nav(n.link) } : undefined }));
    }
    seenRef.current = ids;
  }, [data, nav]);

  const readAll = async () => { await api.post("/notifications/read-all"); qc.invalidateQueries({ queryKey: ["notifications"] }); };
  const open = async (n) => {
    if (!n.read) { await api.post(`/notifications/${n.id}/read`); qc.invalidateQueries({ queryKey: ["notifications"] }); }
    if (n.link) nav(n.link);
  };
  const turnOnPush = async () => {
    try { await enablePush(); setPerm("granted"); toast.success("Bildirimler açıldı"); }
    catch (e) { toast.error(e.message || "Bildirimler açılamadı"); }
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
          <Bell className="h-5 w-5" />
          {unread > 0 && <span className="absolute top-1 right-1 h-4 min-w-4 px-1 rounded-full bg-rose-500 text-white text-[9px] grid place-items-center" data-testid="notification-count">{unread}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between p-3 border-b">
          <span className="font-semibold text-sm">Bildirimler</span>
          {unread > 0 && <button onClick={readAll} className="text-xs text-primary flex items-center gap-1" data-testid="mark-all-read"><Check className="h-3 w-3" /> Tümünü okundu işaretle</button>}
        </div>
        {pushSupported() && perm !== "granted" && (
          <button onClick={turnOnPush} data-testid="enable-push"
            className="w-full flex items-center gap-2 px-3 py-2.5 text-xs text-indigo-600 bg-indigo-50 dark:bg-indigo-950/40 hover:bg-indigo-100 transition-colors border-b">
            <BellRing className="h-4 w-4" /> Anlık bildirimleri aç (uygulama kapalıyken bile)
          </button>
        )}
        <ScrollArea className="h-80">
          {data.length === 0 ? <EmptyState icon={Bell} title="Bildirim yok" /> : (
            <div className="divide-y">
              {data.map((n) => (
                <button key={n.id} onClick={() => open(n)} className={`w-full text-left p-3 hover:bg-muted/60 transition-colors ${!n.read ? "bg-accent/40" : ""}`} data-testid={`notification-item-${n.id}`}>
                  <div className="flex items-start gap-2">
                    {!n.read && <span className="h-2 w-2 rounded-full bg-indigo-500 mt-1.5 shrink-0" />}
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{n.title}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2">{n.body}</p>
                      <p className="text-[10px] text-muted-foreground mt-1">{fromNow(n.created_at)}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
